import { SkipMainLink } from "@clones/ui";
import { useEffect } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { Settings } from "lucide-react";
import { buttonVariants, cn, ErrorState, Spinner } from "@clones/ui";
import { useCurrentRespondent } from "@/auth/queries";
import { OfflineIndicator } from "./offline-indicator";
import { ImpersonationBanner } from "./impersonation-banner";
import { ThemeControls } from "./theme-controls";
import { UserMenu } from "./user-menu";

interface Props {
  children: React.ReactNode;
}

/**
 * Authenticated respondent shell.
 *
 * - Calls `/r/auth/me` on mount; redirects to `/login` when unauthenticated.
 * - Unlike the productive shell there is NO scope/host/home_url redirect: the
 *   respondent role is the lowest rung and this is its only surface. We still
 *   defensively bounce any non-respondent account back to `/login` (the
 *   backend would 403 the `/r/*` calls anyway).
 * - Renders a minimal navbar (brand · theme · profile). The interview is a
 *   single focused surface, so there's no primary nav.
 */
export function RespondentAppShell({ children }: Props) {
  const { data: account, isLoading, isError, isFetching, refetch } = useCurrentRespondent();
  const navigate = useNavigate();
  const { location } = useRouterState();

  useEffect(() => {
    if (isLoading || (isError && !account)) return;
    if (!account || account.role !== "respondent") {
      void navigate({
        to: "/login",
        search: { redirect: location.pathname },
        replace: true,
      });
    }
  }, [account, isLoading, isError, navigate, location.pathname]);

  if (isError && !account) {
    return <ErrorState onRetry={() => void refetch()} retrying={isFetching} />;
  }

  if (isLoading || !account || account.role !== "respondent") {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="studio-shell flex min-h-dvh flex-col bg-background">
      <ImpersonationBanner />
      <SkipMainLink />
      <OfflineIndicator />
      <header className="studio-topbar sticky top-0 z-30 h-[var(--topbar)]">
        <div className="studio-reading flex h-full items-center gap-[var(--control-gap)] px-[var(--page-gutter)]">
          <Link to="/" className="flex min-h-[var(--control-size)] min-w-0 items-center gap-[var(--control-gap)]">
            <span className="min-w-0 font-semibold leading-tight tracking-tight text-sm sm:text-base">
              Цифровые двойники
            </span>
            <span className="hidden text-xs text-muted-foreground sm:inline">Интервью</span>
          </Link>
          <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-[var(--control-gap)]">
            <Link
              to="/settings"
              aria-label="Настройки аккаунта"
              className={cn(buttonVariants({ variant: "ghost", size: "icon" }))}
            >
              <Settings className="h-4 w-4" />
            </Link>
            <ThemeControls />
            <UserMenu />
          </div>
        </div>
      </header>
      <main id="main-content" tabIndex={-1} className="flex-1 min-h-0 min-w-0">{children}</main>
    </div>
  );
}
